import React, { useCallback, useEffect } from 'react';
import {
  Route, Redirect, Switch, useHistory, useLocation,
} from 'react-router-dom';
import { withI18n } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import shortid from 'shortid';
import Template from '@/components/Layout/Template/index';
import { onChangeRouteEventTagManager } from '@/utils/ThirdParties/tagManager';
import { routesV2 } from '@/routes/routes';
import { setBreadCrumb } from '@/redux/actions/breadCrumbs';
import redirects from '@/routes/redirects';
import generateBreadCrumbs from '@/routes/generateBreadCrumbs';
import NewSitePage from '@/pages/NewSite/NewSitePage';
import FeedbackSite from '@/pages/NewSite/FeedbackSite/index';
import EmailVerify from '@/pages/EmailVerify';
import PhoneVerify from '@/pages/PhoneVerify';
import Error404Page from '@/pages/Error404Page/index';
import RedirectToWHMPage from '@/pages/SSO/RedirectToWHMPage';
import RedirectToSiteBuilderPage from '@/pages/SSO/RedirectToSiteBuilderPage/index';
import { handleLastPath } from '@/utils/Formatters/lastPath';
import { commonActions, productsActions } from '@/redux/modules/';
import Loader from '@/components/Layout/Loader';
import { queryParams } from '@/utils/Application/queryParams';
import {
  BRAVO_CHANGE_PHONE_FLOW, BRAVO_EMAIL_FLOW,
  EXPERIENCE_ACCOUNT_SECURITY,
} from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import * as featureFlags from '@/config/GrowthBook/constants';
import { Growthbook } from '@/config/GrowthBook';
import { OnboardingHandler } from '../Onboarding/OnboardingHandler';
import { locale } from '@/utils/locale';
import { useAiChat } from '@/hooks/AiChat';
import { AiChatWrapper } from '@/components/Layout/Template/AiChatWrapper';

let historyListenKey = '';
let onChangeRouteTimeout = null;

const Main = ({ t }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const newSiteFlux = useSelector(state => !state.summary.websiteBuilderDismiss && state.summary.newSiteFlux);
  const emailFlowSkip = useSelector(state => state.emailVerify.emailFlowSkip);
  const emailChangeFlow = useSelector(state => state.emailVerify.emailChangeFlow);
  const emailVerified = useSelector(state => state.summary.emailVerified);
  const phoneNumberVerified = useSelector(state => state.summary.phoneNumberVerified);
  const phoneNumberFlowSkip = useSelector(state => state.phoneNumberVerify.phoneNumberFlowSkip);
  const forceUrl = useSelector(state => state.phoneNumberVerify.forceUrl);
  const phoneNumberChangeFlow = useSelector(state => state.phoneNumberVerify.phoneNumberChangeFlow);
  const dynamicBreadCrumbs = useSelector(state => state.breadCrumbs.dynamicBreadCrumbTitles);
  const { currentChat: currentAiChat } = useSelector(state => state.merlin);
  const bravoEnableEmailFlow = useFeatureIsOn(BRAVO_EMAIL_FLOW);
  const bravoEnablePhoneFlow = useFeatureIsOn(BRAVO_CHANGE_PHONE_FLOW);
  const renderAccountSecurity = useFeatureIsOn(EXPERIENCE_ACCOUNT_SECURITY);
  const enableLogoBuilder = useFeatureIsOn(featureFlags.ENABLE_LOGO_BUILDER);
  const enableStudio = useFeatureIsOn(featureFlags.ENABLE_STUDIO);
  const sitesOnboardingEnabled = useFeatureIsOn(featureFlags.ATIVACAO_SITES_ONBOARDING);
  const useOnboardingAsWizard = useFeatureIsOn(featureFlags.ATIVACAO_ONBOARDING_INSIDE_SITES_WIZARD);
  const enableAdsPage = useFeatureIsOn(featureFlags.ATIVACAO_ENABLE_ADS_PAGE);

  const featureTogglePhoneNumberFlow = useSelector(state => state.featureToggles.toggles && state.featureToggles.toggles.portal.phoneNumberFlow);
  const featureToggleEmailNumberFlow = useSelector(state => state.featureToggles.toggles && state.featureToggles.toggles.portal.emailFlow);
  const { id: userId, onboarding } = useSelector(state => state.summary);
  const products = useSelector(state => state.products);
  const isRootRoute = history.location.pathname === '/';

  const { handleAiChatRender } = useAiChat();

  const ssoRedirectWHMP = history.location.pathname.includes(t('routes.whmRedirectAccess'));
  const ssoRedirectSiteBuilder = history.location.pathname.includes(t('routes.siteBuilderRedirectAccess'));

  const pathEmailsList = `${t('routes.emailsList')}`;
  const pathTitanUpgrade = `${t('routes.titanUpgrade')}/`;
  const pathTitanInboxUpgrade = `${t('routes.titanInboxUpgrade')}/`;

  const isTitanUrl = location.pathname.includes(pathEmailsList) || location.pathname.includes(pathEmailsList.concat(pathTitanUpgrade)) || location.pathname.includes(pathEmailsList.concat(pathTitanInboxUpgrade));

  const sendFlagsToRedux = useCallback(() => {
    const flags = Object.keys(featureFlags).reduce(
      (list, flag) => ({
        ...list,
        [flag.toLowerCase()]: Growthbook.evalFeature(flag.toLowerCase()).value,
      }),
      {},
    );

    if (flags) {
      dispatch(commonActions.toggles.set(flags));
    }
  }, [dispatch]);

  useEffect(() => {
    sendFlagsToRedux();
  }, [sendFlagsToRedux]);

  const getLocationValue = () => {
    const locationSearch = queryParams(location.search, 'location');

    if (locationSearch !== null) {
      return locationSearch;
    }

    let locationValue = (location.pathname + location.search).replace(/\?/g, '&');
    // Remove first / if it exists
    locationValue = locationValue.startsWith('/') ? locationValue.slice(1) : locationValue;
    // Replace remaining / with + to not have dashes in url and bring problems to GA4 analytics
    locationValue = locationValue.replace(/\//g, '+');
    return locationValue;
  };

  useEffect(() => {
    if (location.pathname !== '/') {
      handleLastPath(location, userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (action === 'PUSH') {
        handleLastPath(location, userId);
      }
    });

    return () => {
      unlisten();
    };
  }, [history, userId]);

  useEffect(() => {
    const unlisten = history.listen((historyListen) => {
      if (historyListenKey !== historyListen.key) {
        clearTimeout(onChangeRouteTimeout);
        historyListenKey = historyListen.key;
        onChangeRouteTimeout = setTimeout(() => {
          onChangeRouteEventTagManager();
        }, 100);
      }
    });

    return () => {
      unlisten();
      clearTimeout(onChangeRouteTimeout);
    };
  }, [history]);

  const getBreadCrumbs = (props, Component) => {
    const isMobile = window.innerWidth < 700;
    const { match } = props;

    if (match.path === '*' && !isMobile) {
      return <Component {...props} />;
    }

    const breadCrumbs = generateBreadCrumbs(props, t, dynamicBreadCrumbs);
    dispatch(setBreadCrumb(breadCrumbs));

    return <Component {...props} />;
  };

  useEffect(() => {
    if (isRootRoute) {
      dispatch(productsActions.products.request({ history }));
    }
  }, [dispatch, history, isRootRoute]);

  const getRender = () => {
    const isInSitebuilderSsoPage = history.location.pathname.includes(`${locale('routes.siteBuilderRedirectAccess')}`);

    const shouldRenderOnboarding = sitesOnboardingEnabled
      && onboarding
      && onboarding.sites
      && !!onboarding.sites.length
      && !isInSitebuilderSsoPage;

    if (shouldRenderOnboarding) {
      sessionStorage.setItem('onboardingRendered', shouldRenderOnboarding);
    }

    const onboardingRendered = sessionStorage.getItem('onboardingRendered');
    const onboardingFinished = sessionStorage.getItem('onboardingFinished');

    const allRoutes = routesV2(t, undefined, {
      renderAccountSecurity,
      enableLogoBuilder,
      useOnboardingAsWizard,
      enableStudio,
      enableAdsPage,
    }).map(({
      path, Component, full, beta,
    }) => (
      <Route
        exact
        path={path}
        key={shortid.generate}
        render={props => (
          full ? (
            <Component {...props} />
          ) : (
            <Template beta={beta}>
              <AiChatWrapper>
                {getBreadCrumbs(props, Component)}
              </AiChatWrapper>
            </Template>
          )
        )}
      />
    ));

    if (shouldRenderOnboarding && !onboardingFinished) {
      return (
        <Switch>
          <Route exact path={`${t('routes.onboarding')}`} component={OnboardingHandler} />
          <Redirect to={`${t('routes.onboarding')}`} />
          {allRoutes}
        </Switch>
      );
    }


    if (ssoRedirectWHMP) {
      return (
        <Switch>
          <Route exact path={`${t('routes.whmRedirectAccess')}/:id`} component={RedirectToWHMPage} />
          <Route path="*" component={Error404Page} />
        </Switch>
      );
    }

    if (ssoRedirectSiteBuilder) {
      return (
        <Switch>
          <Route exact path={`${t('routes.siteBuilderRedirectAccess')}/:domain/:hostingId?`} component={RedirectToSiteBuilderPage} />
          <Route path="*" component={Error404Page} />
        </Switch>
      );
    }

    if (!shouldRenderOnboarding && !onboardingRendered && (
      (bravoEnableEmailFlow && featureToggleEmailNumberFlow && !isTitanUrl && (!emailFlowSkip && !emailVerified)) || (emailChangeFlow)
    )) {
      return (
        <Switch>
          <Route exact path={`${t('routes.emailVerify')}/:changeFlow?`} component={EmailVerify} />
          <Redirect to={`${t('routes.emailVerify')}${emailChangeFlow ? '/alterar' : ''}?location=${getLocationValue()}`} />
        </Switch>
      );
    }

    if (!shouldRenderOnboarding && !onboardingRendered && ((bravoEnablePhoneFlow && featureTogglePhoneNumberFlow && !isTitanUrl && (!phoneNumberVerified && !phoneNumberFlowSkip)) || (phoneNumberChangeFlow))) {
      return (
        <Switch>
          <Route exact path={`${t('routes.phoneVerify')}/:changeFlow?`} component={PhoneVerify} />
          <Redirect to={`${t('routes.phoneVerify')}${phoneNumberChangeFlow ? '/alterar' : ''}?location=${getLocationValue()}`} />
        </Switch>
      );
    }

    if (!shouldRenderOnboarding && newSiteFlux) {
      return (
        <Switch>
          <Route exact path={t('routes.newSite')} component={NewSitePage} />
          <Route exact path={`${t('routes.newSite')}${t('routes.feedbackSite')}`} component={FeedbackSite} />
          <Redirect to={t('routes.newSite')} />
        </Switch>
      );
    }

    if (!shouldRenderOnboarding && forceUrl) {
      return (
        <Switch>
          {allRoutes}
          <Redirect to={`${t('routes.myData')}`} />
        </Switch>
      );
    }

    return (
      <>
        <Switch>
          {redirects(t).map(({ path, redirectPath }) => (
            <Redirect
              exact
              from={path}
              to={redirectPath}
              key={shortid.generate}
            />
          ))}

          {allRoutes}

          {!shouldRenderOnboarding && forceUrl && (
          <Redirect to={forceUrl} />
          )}
        </Switch>
      </>
    );
  };

  if (isRootRoute && (products.loaded === false || products.loading)) {
    return <Loader />;
  }

  return (
    <>
      {!currentAiChat.fullScreen && handleAiChatRender()}

      {getRender()}
    </>
  );
};

export default withI18n()(Main);
